import axios from 'axios';
import { incognitoHeaders } from '@/lib/incognitoRequest';
const api = `${process.env.VUE_APP_API_ADDRESS}/v1`
const api2 = `${process.env.VUE_APP_API_ADDRESS}/v2`


export default {
  async get(service, request) {
    const queryString = Object.keys(request).map(key => `${key}=${request[key]}`).join('&');
    return await axios.get(`${api}/${service}?${queryString}`)
  },
  // TYPEAHEAD (busy-my-web#507, frame 40000912:58933): `GET v1/suggest?q=<text>` →
  // `{ suggestions: [string] }`, at most three. Its own method rather than `get` because it is the
  // one read in the app that must be CANCELLABLE — a keystroke makes the answer in flight about a
  // sentence the user has already moved past, so the caller hands in an AbortSignal and axios drops
  // the connection. `encodeURIComponent` because the query is free text a person is typing, `&`
  // and `#` included, and `get`'s bare join would truncate it.
  async suggest(q, signal) {
    return await axios.get(`${api}/suggest?q=${encodeURIComponent(q ?? '')}`, signal ? { signal } : {})
  },
  async getV2(service, request) {
    const queryString = Object.keys(request).map(key => `${key}=${request[key]}`).join('&');
    return await axios.get(`${api2}/${service}?${queryString}`)
  },
  async getByDifferentVersion(service, version) {
    let diffApi = api.split('v1')[0] + version;
    return await axios.get(`${diffApi}/${service}`);
  },
  async insertWitDifferentVersion(service, model, version, config = {}) {
    let diffApi = api.split('v1')[0] + version;
    return await axios.post(`${diffApi}/${service}`, model, config);
  },
  async getWithUrl(url, service, request, config) {
    const queryString = Object.keys(request).map(key => `${key}=${request[key]}`).join('&');
    return await axios.get(`${url}/${service}?${queryString}`, config)
  },
  async getById(service, id,) {
    return await axios.get(`${api}/${service}/${id}`)
  },
  // `config` is passed straight to axios, so a caller can hand in an AbortController's `signal`
  // (busy-my-web#503: the chat's ■ stop aborts the question it is still waiting on). Optional and
  // defaulted, so every existing two-argument call sends exactly what it always sent.
  async insert(service, model, config = {}) {
    return await axios.post(`${api}/${service}`, model, config)
  },
  // Upload files to the user's Google Drive (document-srv). Returns { data: { data: { result: [...] } } }.
  async uploadDocument(formData) {
    return await axios.post(`${api}/document/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  // Same, but to the user's PRIVATE Drive area (not a shared/public location). Used when attaching
  // a file to a personal entity like a task or note.
  async uploadDocumentPrivate(formData) {
    return await axios.post(`${api}/document/upload/private`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  // Create a document record that links an uploaded file to an entity (via its timeline).
  async addDocument(model) {
    return await axios.post(`${api}/document`, model)
  },
  // The Ongoing's OWN timeline — where a file attached straight to the story is filed (doc-attach,
  // 2026-10-02). Created on the first ask, the same id after; 404 when the Ongoing is not the
  // caller's. Returns { data: { data: { timelineId } } }.
  async ensureOngoingOwnTimeline(ongoingId) {
    return await axios.post(`${api}/ongoing/${ongoingId}/own-timeline`)
  },
  // List the user's private Drive files (for the attach-from-Drive picker). Returns
  // { data: { data: [ { fileId, filename, contentType, size } ] } }.
  async listPrivateDriveFiles() {
    return await axios.get(`${api}/document/upload/private/files`)
  },
  // Documents attached to an entity, by its timeline. Returns { data: { thumbnails: [{ id, filename,
  // contentType, hasThumbnail }] } }. Download each via `document/download/{id}`.
  async getDocumentsByTimeline(timelineId) {
    return await axios.get(`${api}/document/${timelineId}/list`)
  },
  // Download a document's bytes (auth via the axios session). Returns a blob response.
  // A GET that answers a FILE rather than the { data } envelope — the receipts CSV export
  // (busy-ai-srv#15). It goes through axios like every other call so the session's auth header is
  // attached; a plain <a href> download would arrive signed out.
  async getFile(service, request = {}) {
    const queryString = Object.keys(request).map(key => `${key}=${encodeURIComponent(request[key])}`).join('&')
    const url = queryString ? `${api}/${service}?${queryString}` : `${api}/${service}`
    return await axios.get(url, { responseType: 'blob' })
  },
  async downloadDocument(id) {
    return await axios.get(`${api}/document/download/${id}`, { responseType: 'blob' })
  },
  // Batch: documents for many entity timelines in ONE request (a list view loads all rows at once).
  // Returns { data: [ { id, timelineId, filename, contentType, hasThumbnail } ] }.
  async getDocumentsByTimelines(timelineIds) {
    return await axios.post(`${api}/document/timelines/list`, timelineIds)
  },
  // "What is this, where did it come from, what is it related to" for any object in Busy
  // (busy-timeline-srv#20). `type` is a slug of the shared vocabulary — 'timeline', 'ongoing', … —
  // and the answer carries identity, provenance, the graph edges around it, and (for a timeline)
  // the mail thread itself (acc-account-sync-srv#11). Returns { data: {...} }.
  async getObject(type, id) {
    return await axios.get(`${api}/objects/${type}/${id}`)
  },
  // "Why does Busy.me believe this" for one graph edge (busy-timeline-srv#28): the belief, the
  // signals with their strength, when/who confirmed it, the corrections history, and the raw
  // provenance string. Returns { data: {...} }; 404 when the edge is not the caller's.
  async getRelationshipEvidence(relationshipId) {
    return await axios.get(`${api}/evidence/relationship/${relationshipId}`)
  },
  // The same question asked of a THREAD: every edge touching it, its classes and every correction.
  async getThreadEvidence(timelineId) {
    return await axios.get(`${api}/evidence/thread/${timelineId}`)
  },
  // THE KNOWLEDGE GRAPH's connections (busy-my-web#710, busy-main-bff#60 → busy-graph-srv): every
  // suggested / confirmed connection of ONE item, by its graph node type (`task`, `note`, `event`,
  // `document`, `contact`, `email_thread`, `ongoing` …) and its owning service's id (an email
  // thread's is its timelineId). 404 from a BFF that predates the endpoint — callers hide quietly.
  async getConnections(type, refId) {
    return await axios.get(`${api}/connections/${encodeURIComponent(type)}/${encodeURIComponent(refId)}`)
  },
  // The USER-WIDE suggestions of one type — the Email workspace Briefing's "N Possible
  // connections" (every suggested edge with an email thread on one side; each item also carries
  // its `source` node). 404 from an older BFF.
  async getSuggestedConnections(type = 'email_thread') {
    // The BFF clamps to 200; asking for the most it gives keeps the Briefing's count from stopping
    // at the server's default page of 50 while the list showed something else (Pouria, 2026-11-03).
    return await axios.get(`${api}/connections?state=suggested&type=${encodeURIComponent(type)}&limit=200`)
  },
  // One item's counts — `{ connected, possible }` (the Ongoing Briefing's "N Connections").
  async getConnectionSummary(type, refId) {
    return await axios.get(`${api}/connections/summary/${encodeURIComponent(type)}/${encodeURIComponent(refId)}`)
  },
  // …and the three answers: confirm / reject one edge, or connect two items by hand.
  async confirmConnection(edgeId) {
    return await axios.post(`${api}/connections/${encodeURIComponent(edgeId)}/confirm`, {})
  },
  async rejectConnection(edgeId, reason = null) {
    return await axios.post(`${api}/connections/${encodeURIComponent(edgeId)}/reject`, reason ? { reason } : {})
  },
  async connectItems(a, b) {
    return await axios.post(`${api}/connections`, { a, b })
  },
  // "Was this helpful?" on one possible connection — `value` 'up' | 'down' | '' (taken back), 204.
  // Measures relation quality in busy-graph-srv; a no-op 204 when the graph is off on that BFF.
  async sendConnectionFeedback(edgeId, value = '') {
    return await axios.post(`${api}/connections/${encodeURIComponent(edgeId)}/feedback`, { value })
  },
  // One mail thread, by the timeline it owns: participants, counts and headline in one row.
  async getEmailThread(timelineId) {
    return await axios.get(`${api}/email/threads/${timelineId}`)
  },
  async getEmailBody(service, model) {
    return await axios.post(`${api}/${service}`, model)
  },
  async deleteWithRequest(service, model) {
    return await axios.post(`${api}/${service}`, model)
  },
  async deleteWithBody(service, model) {
    return await axios.delete(`${api}/${service}`, {data: {contactIds: model}})

  },
  async toggle(service,) {
    return await axios.post(`${api}/${service}`)
  },
  async update(service, id, model) {
    return await axios.put(`${api}/${service}/${id}`, model)
  },
  async updateWithoutID(service, model) {
    return await axios.put(`${api}/${service}`, model)
  },
  async patch(service, id, patchModel) {
    return await axios.patch(`${api}/${service}/${id}`, patchModel)
  },
  async delete(service, id) {
    return await axios.delete(`${api}/${service}/${id}`)
  },
  async deleteWithoutId(service) {
    return await axios.delete(`${api}/${service}`)
  },
  async getWithPath(service, path, request) {
    const queryString = Object.keys(request).map(key => `${key}=${request[key]}`).join('&');
    return await axios.get(`${api}/${service}/${path}?${queryString}`)
  },
  async getWithPathById(service, id, path) {
    return await axios.get(`${api}/${service}/${id}/${path}`)
  },
  async getWithPathByIdDownload(service, id, path) {
    return await axios.get(`${api}/${service}/${id}/${path}`, { responseType: 'arraybuffer' })
  },
  async downloadByUrl(path) {
    return await axios.get(`${api}/${path}`, { responseType: 'arraybuffer' })
  },
  async insertWithPath(service, path, model) {
    return await axios.post(`${api}/${service}/${path}`, model)
  },
  async updateWithPath(service, id, path, model) {
    return await axios.put(`${api}/${service}/${id}/${path}`, model)
  },
  async patchWithPath(service, id, path, model) {
    const patchModel = model.map(c => { return { "op": "replace", "path": `/${c.key}`, "value": c.val }; })
    return await axios.patch(`${api}/${service}/${id}/${path}`, patchModel)
  },
  async patchWithPathNoMapping(service, id, path, model) {
    return await axios.patch(`${api}/${service}/${id}/${path}`, model);
  },
  async deleteWithPath(service, id, path) {
    return await axios.delete(`${api}/${service}/${id}/${path}`)
  },

  uploadWithoutId(service, formData) {
    return axios.post(`${api}/${service}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
  },

  upload(service, id, formData) {
    return axios.post(`${api}/${service}/${id}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
  },

  uploadWithPath(service, id, path, formData) {
    return axios.post(`${api}/${service}/${id}/${path}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
  },

  // SSE streaming POST — yields parsed { type, data } events via async generator.
  // Caller passes an AbortSignal to cancel. Each SSE event is { type: string, data: any }.
  async *streamPost(service, body, signal) {
    const response = await fetch(`${api}/${service}`, {
      method: 'POST',
      // `fetch` does not go through the axios interceptor, so incognito is added here too
      // (busy-main-bff#43): the streaming ask is the ask this mode is most about.
      headers: { 'Content-Type': 'application/json', 'X-CSRF': '1', ...incognitoHeaders(service) },
      credentials: 'include',
      body: JSON.stringify(body),
      signal,
    })

    if (response.status === 429) {
      const body = await response.json().catch(() => ({}))
      const err = new Error(body.message || 'Daily AI request limit reached. Upgrade to Pro for unlimited access.')
      err.status = 429
      throw err
    }
    if (!response.ok) throw new Error(`Stream request failed: ${response.status}`)

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      const parts = buffer.split('\n\n')
      buffer = parts.pop() // keep incomplete trailing chunk

      for (const part of parts) {
        if (!part.trim()) continue
        let type = 'message'
        let dataLine = ''
        for (const line of part.split('\n')) {
          if (line.startsWith('event: ')) type = line.slice(7).trim()
          else if (line.startsWith('data: ')) dataLine = line.slice(6).trim()
        }
        if (!dataLine) continue
        let parsed
        try { parsed = JSON.parse(dataLine) } catch { parsed = dataLine }
        yield { type, data: parsed }
      }
    }
  },
}
